import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";

export default function ClaimCheckPrint() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/bookings/${bookingId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [bookingId]);

  if (err) return <div className="pt-24 text-center text-red-600" data-testid="claim-print-error">{err}</div>;
  if (!data) return <div className="pt-24 text-center text-slate-500">Loading…</div>;
  const b = data.booking;
  const c = data.claim_check;
  if (!c) return <div className="pt-24 text-center text-slate-500">No claim check has been generated for this booking yet.</div>;
  const biz = c.business_snapshot || {};
  const ft = c.function_test || {};

  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 print-page" data-testid="claim-check-print">
      <div className="no-print flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Claim Check preview</h1>
        <button data-testid="print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="border border-slate-300 rounded-2xl bg-white p-8 print-page">
        {/* Header */}
        <div className="flex items-baseline justify-between border-b border-slate-300 pb-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Claim Check / Service Receipt</div>
            <h2 className="text-2xl font-bold text-slate-900 mt-1">{biz.legal_business_name || "The Repair Nook"}</h2>
            <div className="text-xs text-slate-600 mt-1 whitespace-pre-line">{biz.address || ""}</div>
            <div className="text-xs text-slate-600">{biz.phone || ""} · {biz.email || ""}</div>
            {biz.ca_esd_registration && <div className="text-xs text-slate-600">California ESD Reg. No. {biz.ca_esd_registration}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Claim #</div>
            <div className="font-mono font-bold text-lg text-slate-900" data-testid="claim-number">{c.claim_number}</div>
            <div className="text-[10px] text-slate-500 mt-2">Accepted {new Date(c.created_at).toLocaleString()}</div>
          </div>
        </div>

        {/* Customer / Device */}
        <div className="grid grid-cols-2 gap-6 text-xs mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Customer</div>
            <div className="text-slate-900 font-semibold">{c.customer?.name}</div>
            <div className="text-slate-700">{c.customer?.street}</div>
            <div className="text-slate-700">{c.customer?.city}{c.customer?.city ? "," : ""} {c.customer?.state} {c.customer?.zip}</div>
            <div className="text-slate-700">{c.customer?.phone}</div>
            <div className="text-slate-700">{c.customer?.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Device</div>
            <div className="text-slate-900">{c.device?.manufacturer} {c.device?.model_name}</div>
            <div className="text-slate-700">Serial: {c.device?.serial || "—"}</div>
            {c.device?.imei && <div className="text-slate-700">IMEI: {c.device.imei}</div>}
            <div className="text-slate-700">Color: {c.device?.color || "—"}</div>
            <div className="text-slate-700">Accessories: {c.device?.accessories || "—"}</div>
            <div className="text-slate-700">Case: {c.device?.case_present ? "Yes" : "No"} · SIM: {c.device?.sim_present ? "Yes" : "No"}</div>
          </div>
        </div>

        {/* Reported problem */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Customer-reported problem</div>
          <div className="text-xs text-slate-800 whitespace-pre-line">{b.customer?.notes || "(none)"}</div>
        </div>

        {/* Pre-repair condition + intake test */}
        <div className="grid grid-cols-2 gap-6 text-xs mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pre-repair condition</div>
            <ul className="list-disc pl-4 text-slate-800">
              {(c.pre_repair_condition || []).length ? c.pre_repair_condition.map((x, i) => <li key={i}>{x}</li>) : <li className="text-slate-400">None noted</li>}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Intake function test</div>
            <table className="w-full text-[11px]">
              <tbody>
                {Object.keys(ft).length === 0 && <tr><td className="text-slate-400">Not recorded</td></tr>}
                {Object.entries(ft).map(([k, v]) => (
                  <tr key={k}><td className="capitalize text-slate-700">{k}</td><td className="text-right font-mono">{v}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Diagnostic / service charges */}
        <div className="text-xs mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Diagnostic / service charges at intake</div>
          <div className="text-slate-800">No charges assessed at intake unless separately authorized. The final written repair estimate follows physical inspection.</div>
        </div>

        {/* CA statutory notice */}
        <div className="border border-amber-300 bg-amber-50 rounded-xl p-3 text-[11px] leading-snug text-amber-900 mb-4">
          <div className="uppercase tracking-widest text-[9px] font-semibold text-amber-800 mb-1">California Required Claim Check Notice</div>
          {c.ca_notice_version
            ? <div className="whitespace-pre-line" data-testid="ca-notice">{c.ca_notice_version}</div>
            : <div className="italic" data-testid="ca-notice-missing">
                California Required Claim Check Notice has not been configured in Admin → Business Settings. Do not hand this claim check to a customer until the exact BHGS statutory notice is entered.
              </div>}
        </div>

        {/* Technician signoff */}
        <div className="grid grid-cols-2 gap-6 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Technician</div>
            <div className="font-semibold text-slate-900">{c.technician}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Customer signature</div>
            <div className="border-b border-slate-400 h-6 mt-3"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
